import React, { Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { ThemeContext } from '../context/ThemeContext';
import TypewriterText from '../components/styled/TypewriterText';
import { loadSlim } from "@tsparticles/slim";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { socialLinks as socials } from '../data/socialLinks'; // Import the projects array
import SocialLinks from '../components/SocialLinks';
import ResumeButton from '../components/styled/ResumeButton';
import { FeaturedName } from './about/FeaturedName';

// Dynamically import your 3D Model
const ThreeDModel = React.lazy(() => import('../components/ThreeDModel'));

const pageVariants2 = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { when: "beforeChildren", staggerChildren: 0.3 }
    }
};

const fadeIn = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 1 } },
};

const Home = () => {
    const [init, setInit] = useState(false);
    const { theme, themes, particleOption, particleOptions } = useContext(ThemeContext);

    useEffect(() => {
        initParticlesEngine(async (engine) => {
            await loadSlim(engine);
        }).then(() => {
            setInit(true);
        });
    }, []);

    const homeParticleOptions = useMemo(() => {
        return particleOptions[particleOption] || {};
    }, [particleOptions, particleOption]);

    return (
        <>
            <Helmet>
                <title>Shivam Patel IOS</title>
                <meta name="description" content="Welcome to my portfolio website!" />
            </Helmet>

            {/* Particles Background */}
            {init && (
                <Particles
                    id="tsparticles"
                    options={homeParticleOptions}
                />
            )}

            {/* Main Section */}
            <section
                className={`flex flex-col justify-start items-center mb-0 sm:mb-5 p-0 mt-4 sm:mt-9 text-center ${themes[theme].text}`}
                style={{
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                }}
            >
                <TypewriterText text="Shivam Patel" />

                {/*
                    Mobile:  "Walmart" on line 1, "Senior Software Engineer • iOS" on line 2
                    Desktop: single row  "Walmart • Senior Software Engineer • iOS"
                    Font size scales from ~17px (small phones) up to 24px (text-2xl).
                */}
                <motion.div
                    className={`flex flex-wrap items-center justify-center gap-x-2 sm:gap-x-3 gap-y-0.5 px-3 mt-0 sm:mt-3 ${themes[theme].subText}`}
                    variants={fadeIn}
                    initial="initial"
                    animate="animate"
                    style={{ fontSize: 'clamp(1.05rem, 4.6vw, 1.5rem)' }}
                >
                    

                    {/* Mobile View (< sm) */}
                    <div className="flex flex-col items-center justify-center gap-y-0 sm:hidden">
                        <div className="flex items-center gap-x-2">
                            <FeaturedName>{"Sr. Software Engineer"}</FeaturedName>
                            <FeaturedName>{"iOS"}</FeaturedName>
                        </div>
                        <FeaturedName>{"Walmart"}</FeaturedName>
                    </div>

                    {/* Desktop View (>= sm) */}
                    <div className="hidden sm:flex items-center justify-center gap-x-2">
                        <FeaturedName>{"Walmart"}</FeaturedName>
                        <FeaturedName>{"•"}</FeaturedName>
                        <FeaturedName>{"Senior Software Engineer"}</FeaturedName>
                        <FeaturedName>{"•"}</FeaturedName>
                        <FeaturedName>{"iOS"}</FeaturedName>
                    </div>
                </motion.div>

                <motion.div
                    className="flex flex-wrap items-center justify-center gap-6 mb-0 mt-0 sm:mt-3"
                    initial="hidden"
                    animate="visible"
                    variants={pageVariants2}
                >
                    {socials.map((social, index) => (
                        <SocialLinks
                            key={social.id || index}
                            socialId={social.id}
                            imgSrc={social.imgSrc}
                            linkURL={social.linkURL}
                        />
                    ))}
                </motion.div>

                {/* Resume Button with Delay */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: socials.length * 0.4 }} // Delay based on number of socials
                >
                    <ResumeButton link={`https://drive.google.com/file/d/1AB78OlZDogojuTF10K5-J4gK5Kf9J-9W/view?usp=sharing`} />
                </motion.div>
            </section>

            {/* 3D Model Section */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: socials.length * 0.5 }} // Delay based on number of socials
            >
                <Suspense fallback={null}>
                    <ThreeDModel />
                </Suspense >
            </motion.div>
        </>
    );
};

export default Home;
